import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getArticle, getArticleBySlug, getArticles, getSiteSettings, recordArticleView } from '../services/api';
import CommentSection from '../components/news/CommentSection';
import ShareButtons from '../components/news/ShareButtons';
import { Calendar, User, ChevronRight, Clock, Tag, Volume2, Pause, Play, Pencil } from 'lucide-react';
import DOMPurify from 'dompurify';

const FB = 'https://images.unsplash.com/photo-1597678142569-5eaad50ceed1?q=80&w=1200';
const CAT_FALLBACKS = {
  cat_politics: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?q=80&w=800',
  cat_business: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800',
  cat_sports: 'https://images.unsplash.com/photo-1461896836934-bd45ea8dbc33?q=80&w=800',
  cat_entertainment: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=800',
  cat_technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800',
  cat_culture: 'https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?q=80&w=800',
  cat_local: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e13?q=80&w=800',
};
const getFB = (catId) => CAT_FALLBACKS[catId] || FB;
const CC = { cat_politics:'#d32f2f', cat_business:'#1565c0', cat_sports:'#2e7d32', cat_entertainment:'#7b1fa2', cat_technology:'#0277bd', cat_culture:'#e65100', cat_local:'#00695c' };

const SANITIZE_CONFIG = {
  ADD_TAGS: ['iframe', 'blockquote'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'loading', 'data-dnt', 'data-instgrm-permalink', 'data-instgrm-version', 'width', 'height']
};

function useLoadSocialEmbeds(htmlString) {
  useEffect(() => {
    if (!htmlString) return;
    if (/twitter-tweet|twitter\.com|x\.com/.test(htmlString)) {
      if (!document.getElementById('twitter-wjs')) {
        const s = document.createElement('script');
        s.id = 'twitter-wjs'; s.async = true; s.src = 'https://platform.twitter.com/widgets.js';
        document.body.appendChild(s);
      } else if (window.twttr?.widgets) {
        window.twttr.widgets.load();
      }
    }
    if (/instagram-media|instagram\.com/.test(htmlString)) {
      if (!document.getElementById('instagram-embed-js')) {
        const s = document.createElement('script');
        s.id = 'instagram-embed-js'; s.async = true; s.src = 'https://www.instagram.com/embed.js';
        document.body.appendChild(s);
      } else if (window.instgrm?.Embeds) {
        window.instgrm.Embeds.process();
      }
    }
  }, [htmlString]);
}

/**
 * Post-process the rendered article body so that consecutive <img> siblings
 * (or wrapped in single-img <p>'s) are grouped into a horizontal swipeable
 * gallery. Single images are left untouched.
 */
function useImageGallery(content, ref) {
  useEffect(() => {
    const root = ref.current;
    if (!root || !content) return;
    // Unwrap <p> that contains a single image so siblings can be detected
    root.querySelectorAll('p').forEach(p => {
      const onlyImg = p.children.length === 1 && p.children[0].tagName === 'IMG' && (p.textContent || '').trim() === '';
      if (onlyImg) {
        const img = p.children[0];
        p.parentNode.insertBefore(img, p);
        p.remove();
      }
    });
    // Walk children and group consecutive imgs
    const children = Array.from(root.children);
    let group = [];
    const wrapGroup = (imgs) => {
      if (imgs.length < 2) return;
      const wrap = document.createElement('div');
      wrap.className = 'article-gallery';
      wrap.setAttribute('data-testid', 'article-gallery');
      const track = document.createElement('div');
      track.className = 'article-gallery-track';
      imgs.forEach(im => {
        const slide = document.createElement('div');
        slide.className = 'article-gallery-slide';
        slide.appendChild(im);
        track.appendChild(slide);
      });
      wrap.appendChild(track);
      // Insert wrap before first img (already moved); first img's original parent was root
      root.insertBefore(wrap, imgs[0].parentNode === track ? null : imgs[0]);
    };
    children.forEach((el) => {
      if (el.tagName === 'IMG') {
        group.push(el);
      } else {
        if (group.length >= 2) wrapGroup(group);
        group = [];
      }
    });
    if (group.length >= 2) wrapGroup(group);
  }, [content, ref]);
}

function ago(d, kn) {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 60) return kn ? `${m} ನಿಮಿಷಗಳ ಹಿಂದೆ` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return kn ? `${h} ಗಂಟೆಗಳ ಹಿಂದೆ` : `${h}h ago`;
  return kn ? `${Math.floor(h/24)} ದಿನಗಳ ಹಿಂದೆ` : `${Math.floor(h/24)}d ago`;
}

/**
 * If the stored content has no HTML structure (raw text from a paste, MS Word,
 * or older editor versions), wrap each line in a <p>. This guarantees the
 * published article has visible paragraph breaks instead of one wall of text.
 */
function ensureHtml(content) {
  if (!content) return '';
  const trimmed = content.trim();
  // Already has block-level HTML? Leave as-is.
  if (/<\s*(p|h[1-6]|ul|ol|blockquote|div|article|section|figure|iframe|table|pre)[\s>]/i.test(trimmed)) {
    return content;
  }
  // Convert each non-empty line to a paragraph; preserve emphasis tags inline.
  const lines = trimmed.split(/\n+/).map(l => l.trim()).filter(Boolean);
  if (!lines.length) return '';
  return lines.map(l => `<p>${l}</p>`).join('\n');
}

function ArticleBody({ content }) {
  const ref = useRef(null);
  const normalised = ensureHtml(content || '');
  useLoadSocialEmbeds(normalised);
  useImageGallery(normalised, ref);
  return <div ref={ref} className="article-body font-kannada" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(normalised, SANITIZE_CONFIG) }} data-testid="article-content" />;
}

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isKannada } = useLanguage();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const utteranceRef = useRef(null);
  const canEdit = user && ['admin', 'editor'].includes(user.role);

  useEffect(() => {
    setLoading(true);
    // The URL param can be either an article_id (art_xxxxx) or a slug.
    // Try article_id first; if it 404s, fall back to slug lookup.
    const looksLikeId = typeof id === 'string' && id.startsWith('art_');
    const fetchArticle = looksLikeId
      ? getArticle(id).catch(() => getArticleBySlug(id))
      : getArticleBySlug(id).catch(() => getArticle(id));
    Promise.all([
      fetchArticle,
      getSiteSettings().catch(() => ({ data: null }))
    ]).then(([artRes, setRes]) => {
      setArticle(artRes.data);
      setCommentsEnabled(setRes.data?.comments_enabled === true);
      const fetchedId = artRes.data?.article_id;
      const fetchedSlug = artRes.data?.slug;
      // Auto-redirect legacy art_xxxx URLs to clean slug URLs without breaking the back button.
      if (fetchedSlug && id !== fetchedSlug && id?.startsWith('art_')) {
        navigate(`/article/${fetchedSlug}`, { replace: true });
      }
      // Fire-and-forget view tracking
      if (fetchedId) {
        recordArticleView(fetchedId).catch(() => {});
      }
      if (artRes.data?.category_id) {
        getArticles({ category: artRes.data.category_id, limit: 5 }).then(r => {
          setRelated((r.data.articles || []).filter(a => a.article_id !== fetchedId).slice(0, 4));
        }).catch(() => {});
      }
      getArticles({ limit: 6 }).then(r => {
        setTrending((r.data.articles || []).filter(a => a.article_id !== fetchedId).slice(0, 4));
      }).catch(() => {});
    }).catch(console.error).finally(() => setLoading(false));
    window.scrollTo(0, 0);
    return () => { window.speechSynthesis?.cancel(); if (utteranceRef.current?.audio) utteranceRef.current.audio.pause(); };
  }, [id]);

  // Load voices once on mount
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices(); // trigger loading
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }
  }, []);

  const toggleReadAloud = () => {
    if (isReading) {
      window.speechSynthesis?.cancel();
      if (utteranceRef.current?.audio) { utteranceRef.current.audio.pause(); }
      setIsReading(false);
      return;
    }
    // Use the same content that's displayed on screen
    const displayedContent = isKannada
      ? ((article.content_kn || '').length > 100 ? (article.content_kn || '') : (article.content || article.content_kn || ''))
      : (article.content || '');
    const plainText = displayedContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    if (!plainText) return;

    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();

    // Find best voice for Kannada
    const voices = synth.getVoices();
    const knVoice = voices.find(v => v.lang === 'kn-IN') 
                 || voices.find(v => v.lang.startsWith('kn'))
                 || voices.find(v => v.lang.includes('IN'))
                 || null;

    // Split into chunks (~800 chars at sentence boundaries)
    const chunks = [];
    let rem = plainText;
    while (rem.length > 0) {
      if (rem.length <= 800) { chunks.push(rem); break; }
      let sp = rem.lastIndexOf('.', 800);
      if (sp < 100) sp = rem.lastIndexOf(' ', 800);
      if (sp < 100) sp = 800;
      chunks.push(rem.substring(0, sp + 1));
      rem = rem.substring(sp + 1).trim();
    }

    let idx = 0;
    const speakChunk = () => {
      if (idx >= chunks.length) { setIsReading(false); return; }
      const u = new SpeechSynthesisUtterance(chunks[idx]);
      u.lang = 'kn-IN';
      if (knVoice) u.voice = knVoice;
      u.rate = 0.85;
      u.pitch = 1;
      u.onend = () => { idx++; speakChunk(); };
      u.onerror = () => { idx++; speakChunk(); };
      synth.speak(u);
    };
    speakChunk();
    setIsReading(true);
    utteranceRef.current = {};
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!article) return <div className="min-h-[60vh] flex items-center justify-center flex-col gap-3"><h2 className="text-xl font-bold">{isKannada ? 'ಲೇಖನ ಕಂಡುಬಂದಿಲ್ಲ' : 'Not Found'}</h2><Link to="/" className="text-red-600 hover:underline text-sm font-bold">{isKannada ? 'ಮುಖಪುಟ' : 'Home'}</Link></div>;

  const title = isKannada ? (article.title_kn || article.title) : article.title;
  // Use longer content between kn and en to avoid showing truncated translations
  const contentKn = article.content_kn || '';
  const contentEn = article.content || '';
  const content = isKannada ? (contentKn.length > 100 ? contentKn : (contentEn || contentKn)) : contentEn;
  const summary = isKannada ? (article.summary_kn || article.summary) : article.summary;
  const catName = article.category ? (isKannada ? (article.category.name_kn || article.category.name) : article.category.name) : '';
  const color = CC[article.category_id] || '#d32f2f';
  const author = article.author_label || article.author?.name || 'ತಾಯ್ನಾಡು ಸುದ್ದಿ ಮನೆ';
  const pubDate = article.published_at ? new Date(article.published_at).toLocaleDateString(isKannada ? 'kn-IN' : 'en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const gT = (a) => isKannada ? (a.title_kn || a.title) : a.title;

  // SEO meta values - prefer admin-set custom fields, fall back to article fields
  const seoTitle = String(article.meta_title || title || 'Taaynadu News').slice(0, 160);
  const seoDesc = String(article.meta_description || summary || (content || '').replace(/<[^>]+>/g, '').slice(0, 200) || 'Taaynadu - Karnataka News').slice(0, 300);
  const seoSlug = article.custom_slug || article.slug || article.article_id;
  const canonicalUrl = `${window.location.origin}/article/${seoSlug}`;
  const seoImage = article.featured_image || `${window.location.origin}/logo.svg`;
  const docTitle = `${seoTitle} | Taaynadu`;

  return (
    <div className="bg-[#f8f8f8] min-h-screen" data-testid="article-page">
      <Helmet>
        <title>{docTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Taaynadu" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:secure_url" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="kn_IN" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={seoImage} />
        {article.published_at ? <meta property="article:published_time" content={article.published_at} /> : null}
        {article.author_label ? <meta property="article:author" content={article.author_label} /> : null}
      </Helmet>

      {/* Floating Edit button for admin / editor */}
      {canEdit && article.article_id && (
        <Link
          to={`/editor/write?edit=${article.article_id}`}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-all"
          title={isKannada ? 'ಸಂಪಾದಿಸಿ' : 'Edit article'}
          data-testid="floating-edit-btn"
        >
          <Pencil className="w-5 h-5" />
        </Link>
      )}

      <div className="max-w-[1400px] mx-auto px-4 py-5">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-[12px] text-gray-500 mb-4">
          <Link to="/" className="hover:text-red-600">{isKannada ? 'ಮುಖಪುಟ' : 'Home'}</Link>
          <ChevronRight className="w-3 h-3" />
          {catName && <><Link to={`/category/${article.category_id}`} className="hover:text-red-600">{catName}</Link><ChevronRight className="w-3 h-3" /></>}
        </nav>

        <div className="grid grid-cols-12 gap-5">
          {/* Article */}
          <article className="col-span-12 lg:col-span-8 bg-white border border-gray-100 rounded-lg p-5 lg:p-7">
            {catName && <span className="inline-block px-2.5 py-0.5 text-[11px] font-bold text-white rounded mb-3" style={{ backgroundColor: color }}>{catName}</span>}
            <h1 className="text-[28px] lg:text-[34px] font-black text-[#1a1a1a] leading-tight mb-3 font-kannada" data-testid="article-title">{title}</h1>
            {summary && <p className="text-[16px] text-gray-600 leading-relaxed mb-4 font-baloo border-l-3 border-red-600 pl-4">{summary}</p>}
            <div className="flex flex-wrap items-center gap-3 text-[12px] text-gray-500 mb-5 pb-4 border-b border-gray-200">
              <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /><span className="font-bold text-gray-700">{author}</span></div>
              {pubDate && <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /><span>{pubDate}</span></div>}
              {/* Read Aloud */}
              <button onClick={toggleReadAloud} className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] font-bold transition-colors ${isReading ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300 hover:border-red-600 hover:text-red-600'}`} data-testid="read-aloud-btn">
                {isReading ? <><Pause className="w-3 h-3" /> {isKannada ? 'ನಿಲ್ಲಿಸಿ' : 'Stop'}</> : <><Volume2 className="w-3 h-3" /> {isKannada ? 'ಓದಿ ಹೇಳಿ' : 'Read Aloud'}</>}
              </button>
              <div className="ml-auto"><ShareButtons article={article} /></div>
            </div>
            {article.featured_image && (
              <div className="mb-5">
                <img src={article.featured_image} alt={title} className="w-full rounded-lg" onError={(e) => { e.target.src = getFB(article.category_id); }} data-testid="article-hero-image" />
                {article.photo_caption && <p className="text-[11px] text-gray-500 italic mt-2">{article.photo_caption}</p>}
              </div>
            )}
            <ArticleBody content={content} />
            {(article.tag_objects || article.tags)?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-6 pt-4 border-t border-gray-200">
                {(article.tag_objects || []).map(tag => (
                  <span key={tag.id || tag} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-gray-600 bg-gray-100 rounded">
                    <Tag className="w-3 h-3" />{isKannada ? (tag.name_kn || tag.name) : tag.name}
                  </span>
                ))}
                {!article.tag_objects && (article.tags || []).map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-gray-600 bg-gray-100 rounded">
                    <Tag className="w-3 h-3" />{tag.replace('tag_', '').replace('_', ' ')}
                  </span>
                ))}
              </div>
            )}
            {/* Comments - controlled by admin */}
            {commentsEnabled && (
              <div className="mt-6 pt-4 border-t border-gray-200"><CommentSection articleId={article.article_id} /></div>
            )}
            {/* Trending news at end of article for continued engagement */}
            {trending.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-[18px] font-black text-[#1a1a1a] mb-4 font-kannada">{isKannada ? 'ಟ್ರೆಂಡಿಂಗ್ ಸುದ್ದಿಗಳು' : 'Trending News'}</h3>
                <div className="grid grid-cols-2 gap-4">
                  {trending.map(a => (
                    <Link key={a.article_id} to={`/article/${a.slug || a.article_id}`} className="group flex gap-3">
                      <div className="w-20 h-16 rounded overflow-hidden shrink-0">
                        <img src={a.featured_image || getFB(a.category_id)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = getFB(a.category_id); }} />
                      </div>
                      <h4 className="text-[13px] font-bold text-[#1a1a1a] line-clamp-2 leading-snug group-hover:text-red-600 font-kannada">{gT(a)}</h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-4">
            {related.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-lg">
                <div className="bg-red-600 text-white px-4 py-2.5 rounded-t-lg">
                  <h3 className="text-[15px] font-bold">{isKannada ? 'ಸಂಬಂಧಿತ ಸುದ್ದಿ' : 'Related News'}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {related.map(a => (
                    <Link key={a.article_id} to={`/article/${a.slug || a.article_id}`} className="flex gap-3 p-3 hover:bg-gray-50 group" data-testid={`related-${a.article_id}`}>
                      <div className="w-20 h-16 rounded overflow-hidden shrink-0">
                        <img src={a.featured_image || getFB(a.category_id)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = getFB(a.category_id); }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[13px] font-bold text-[#1a1a1a] line-clamp-2 leading-snug group-hover:text-red-600 font-kannada">{gT(a)}</h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
